import {
  ModuleGroupKey,
  groupForModule,
} from 'src/ui/components/sdu-dashboard/modules';
import { TimeGrain, UnitUsageRecord } from 'src/ui/graph/types';

export interface SduDashboardConnection {
  endpoint: string;
  id: string;
  index: number;
  name: string;
}

export interface SduEndpointTableRow {
  canDrillDown: boolean;
  endpointId: string;
  // ISO timestamp of the latest COMPLETED sync run for this endpoint URL
  // across every connection to it (Launchpad). Null when no sync has
  // completed for the URL yet.
  lastSynced: string | null;
  name: string;
  // null = no usage data for this endpoint (e.g. owned but never synced). 0 =
  // synced with zero consumption. The table renders these differently.
  totalUnits: number | null;
  url: string;
}

export interface SduChartPoint {
  date: string;
  totalUnitsConsumed: number;
}

export interface SduBreakdownItem {
  group: ModuleGroupKey;
  totalUnitsConsumed: number;
}

const padZero = (n: number): string => String(n).padStart(2, '0');

const formatLocalDate = (d: Date): string =>
  `${d.getFullYear()}-${padZero(d.getMonth() + 1)}-${padZero(d.getDate())}`;

const formatUtcDate = (d: Date): string => d.toISOString().slice(0, 10);

export const formatDateForApi = (d: Date | null): string | null =>
  d ? formatLocalDate(d) : null;

const isoMondayOf = (dateStr: string): string => {
  const d = new Date(`${dateStr}T00:00:00Z`);
  const day = d.getUTCDay() || 7;
  d.setUTCDate(d.getUTCDate() - (day - 1));
  return formatUtcDate(d);
};

// Jan 4 is always in ISO week 1; from its Monday we step forward by full weeks.
const isoWeekToMonday = (year: number, week: number): string => {
  const jan4 = new Date(Date.UTC(year, 0, 4));
  const jan4Day = jan4.getUTCDay() || 7;
  jan4.setUTCDate(jan4.getUTCDate() - (jan4Day - 1) + (week - 1) * 7);
  return formatUtcDate(jan4);
};

const isoFirstOfMonth = (dateStr: string): string =>
  `${dateStr.slice(0, 7)}-01`;

type UsageRecord = NonNullable<UnitUsageRecord>;

const filterValid = (
  records: ReadonlyArray<UsageRecord | null>
): UsageRecord[] => records.filter((r): r is UsageRecord => r !== null);

const bucketKeyFor = (
  record: UsageRecord,
  timeGrain: TimeGrain
): string | null => {
  if (timeGrain === TimeGrain.Month) {
    if (record.date) {
      return isoFirstOfMonth(record.date);
    }
    if (record.year && record.month) {
      return `${record.year}-${String(record.month).padStart(2, '0')}-01`;
    }
    return null;
  }
  if (timeGrain === TimeGrain.Week) {
    if (record.date) {
      return isoMondayOf(record.date);
    }
    if (record.year && record.week) {
      return isoWeekToMonday(record.year, record.week);
    }
    return null;
  }
  return record.date ?? null;
};

export const aggregateByDate = (
  records: ReadonlyArray<UsageRecord | null>,
  timeGrain: TimeGrain
): SduChartPoint[] => {
  const buckets = new Map<string, number>();
  filterValid(records).forEach((r) => {
    const key = bucketKeyFor(r, timeGrain);
    if (key) {
      buckets.set(key, (buckets.get(key) ?? 0) + r.totalUnitsConsumed);
    }
  });
  return Array.from(buckets.entries())
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([date, totalUnitsConsumed]) => ({ date, totalUnitsConsumed }));
};

export const aggregateByGroup = (
  records: ReadonlyArray<UsageRecord | null>
): SduBreakdownItem[] => {
  // Modules without a defined display group are excluded; their consumption
  // still counts toward the all-time total tile elsewhere.
  const buckets = new Map<ModuleGroupKey, number>();
  filterValid(records).forEach((r) => {
    const group = groupForModule(r.module);
    if (group === null) {
      return;
    }
    buckets.set(group, (buckets.get(group) ?? 0) + r.totalUnitsConsumed);
  });
  return Array.from(buckets.entries())
    .filter(([, total]) => total > 0)
    .map(([group, totalUnitsConsumed]) => ({ group, totalUnitsConsumed }));
};

// Picks the connection to back an endpoint URL row when multiple owned
// connections point at the same URL. Prefers the most recently synced
// connection (so future "sync this endpoint" retries start with proven
// credentials), falling back to alphabetical-by-name. When sync timestamps
// aren't available the behavior collapses to plain alphabetical.
export const pickRepresentativeConnection = (
  matching: readonly SduDashboardConnection[],
  lastSyncedByConnectionId?: ReadonlyMap<string, string | null>
): SduDashboardConnection | null => {
  if (!matching.length) {
    return null;
  }

  return [...matching].sort((a, b) => {
    const aSync = lastSyncedByConnectionId?.get(a.id) ?? null;
    const bSync = lastSyncedByConnectionId?.get(b.id) ?? null;
    if (aSync !== bSync) {
      if (aSync === null) {
        return 1;
      }
      if (bSync === null) {
        return -1;
      }
      return bSync.localeCompare(aSync);
    }
    return a.name.localeCompare(b.name);
  })[0];
};

export const aggregateByEndpoint = (
  records: ReadonlyArray<UsageRecord | null>,
  connections: readonly SduDashboardConnection[],
  lastSyncedByConnectionId?: ReadonlyMap<string, string | null>,
  lastSyncedByEndpoint?: ReadonlyMap<string, string | null>
): SduEndpointTableRow[] => {
  const totalsByEndpoint = new Map<string, number>();
  filterValid(records).forEach((r) => {
    totalsByEndpoint.set(
      r.endpoint,
      (totalsByEndpoint.get(r.endpoint) ?? 0) + r.totalUnitsConsumed
    );
  });

  // Row set = every endpoint URL with usage data, plus every endpoint URL
  // the user has an owned connection for. Endpoints without an owned
  // connection render with canDrillDown = false (no link to drill into).
  // Endpoints with an owned connection but no usage data appear with
  // totalUnits = null so the table can render a "—" placeholder.
  const endpointSet = new Set<string>(totalsByEndpoint.keys());
  connections.forEach((c) => endpointSet.add(c.endpoint));
  return Array.from(endpointSet).map(
    (endpoint): SduEndpointTableRow => {
      const matching = connections.filter((c) => c.endpoint === endpoint);
      const representative = pickRepresentativeConnection(
        matching,
        lastSyncedByConnectionId
      );
      const total = totalsByEndpoint.has(endpoint)
        ? totalsByEndpoint.get(endpoint) ?? 0
        : null;
      const lastSynced = lastSyncedByEndpoint?.get(endpoint) ?? null;
      if (representative === null) {
        return {
          canDrillDown: false,
          endpointId: endpoint,
          lastSynced,
          name: endpoint,
          totalUnits: total,
          url: endpoint,
        };
      }
      return {
        canDrillDown: true,
        endpointId: String(representative.index),
        lastSynced,
        name: representative.name,
        totalUnits: total,
        url: endpoint,
      };
    }
  );
};

const NUMBER_FORMATTER = new Intl.NumberFormat('en-US');

export const formatTotalUnits = (n: number): string =>
  NUMBER_FORMATTER.format(Math.round(n));
